import { Event, StatefulEvent } from "../../../../../Global/Base/Events";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { ComponentTreeType } from "../../../LogicalTree/Definitions";
import { FrameSelChanged, IActualObjectChangedEventArgs } from "../Frames";
import { IFrame, IFrameWithRights } from "../IFrame";
import { IMasterableFrame, IMasterableFrameWithDataGrid } from "./FrameInterfaces";

/**
     * Rozhraní pro obecnou implementaci framu, ktery obsahuje detail
     *
     * @includeToDoc
     */
export interface IDetailMasterableFrame extends IMasterableFrame {
    detail: IFrame | null;
    detailForm: string | null;

    readonly selectionChanged: Event<IMasterableFrame, FrameSelChanged>;
    readonly changesCanceled: Event<IMasterableFrame, void>;
    readonly recordChangesCanceled: Event<IMasterableFrame, string>;
    readonly editingRow: Event<IFrame, OWObject>;
    readonly actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs>;
    readonly actualObjectSaved: Event<IMasterableFrame, OWObject>;
    readonly actualObjectValueChanged: Event<IFrame, OWObject>;
    readonly initialized: StatefulEvent<IFrame, void>;
    readonly dataLoaded: Event<IMasterableFrame, any>;
    readonly dataLoadingCancelled: Event<IMasterableFrame, any>;
    readonly dataLoadingFailed: Event<IMasterableFrame, any>;
    /**
     * TODO-rites: ? bude potřeba?
     */
    readonly detailComponentTree: ComponentTreeType | null;

    /**
     * Příznak, jestli je detail otevřen v modálním okně
     * @internal
     */
    hasOpenedDetailInModal?: boolean;
    /**
     * Příznak zda form podporuje editaci více záznamů současně
     * @internal
     */
    supportsMultiEditing?: boolean;
}

export class IDetailMasterableFrame {
    private static _properties = [
        "detail", "detailForm", "selectionChanged", "changesCanceled", "recordChangesCanceled", "editingRow",
        "actualObjectChanged", "actualObjectSaved", "actualObjectValueChanged",
        "initialized", "dataLoaded", "dataLoadingCancelled", "dataLoadingFailed", "detailComponentTree",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * Interface pro použiti z DetailFrame
 *
 * @generateInstanceClass
 */
export interface IDetailFrame extends IFrameWithRights {
    readonly canUseLikeDetail: boolean;
    readonly detailResized: Event<any, any>;
    readonly actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs>;
    readonly actualObjectValueChanged: Event<IFrame, OWObject>;
    readonly changesCanceled: Event<IFrame, void>;
    readonly editingRow: Event<IFrame, OWObject>;
    /**
     * TODO-rites: ? bude potřeba?
     */
    readonly mastersSuperComponentTree: ComponentTreeType | null;
    // Příznak, že se má pro práva ignorovat master. Třída na něm může mít s třídou objektu v detailu vztah předek - potomek a tím pádem jinak nastavená práva.
    readonly ignoreMasterRightsObject?: boolean;
    setDetailToEditMode(isEditing: boolean, alsoMasterListFrame: boolean): Promise<void>;
}

export class IDetailFrame {
    private static _properties: (keyof (IDetailFrame))[] = ["canUseLikeDetail", "detailResized", "actualObjectChanged", "actualObjectValueChanged", "changesCanceled", "editingRow", "mastersSuperComponentTree"];
    private static _methods: (keyof (IDetailFrame))[] = ["setDetailToEditMode"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IFrame;
    }
}

/**
 * Interface pro použiti z SeparateDetailFrame
 *
 * @generateInstanceClass
 */
export interface ISeparateDetailFrame extends IDetailFrame {
    readonly canUseLikeSeparateDetailFrame: boolean;
    readonly hideCloseButton: boolean;
}

export class ISeparateDetailFrame {
    private static _properties = ["canUseLikeSeparateDetailFrame"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IFrame;
    }
}

/**
 * Interface pro samostatně uložitelný detail
 * @generateInstanceClass
 */
export interface ISaveable {
    /** Uloží záznam/objekt/frame */
    save(skipSavingEditContextId?: boolean): Promise<SaveResultData[] | null>;
}

export class ISaveable {
    private static _properties: (keyof (ISaveable))[] = [];
    private static _methods: (keyof (ISaveable))[] = ["save"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * @includeToDoc
 * @generateInstanceClass
 */
export interface ICalendarFrame extends IDetailMasterableFrame, IMasterableFrameWithDataGrid {
    /**
     * Aktuální dataGrid, pokud jich má frame víc
     */
    actualObjectComplexKey: string;
    /**
     * Návratové hodnoty:
     * null - uložení proběhlo v pořádku
     * prázdné pole - uložení se nepodařilo, ale není co zpracovat
     * naplněné pole - vrací vyjímky vzniklé při ukládání
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     */
    saveRecords(id?: string, checkRecommended?: boolean): Promise<SaveResultData[] | null>;
    /**
     * Zruší editaci
     */
    cancelChanges(): Promise<void>;
}

export class ICalendarFrame {
    private static _properties = ["actualObjectComplexKey"];
    private static _methods: (keyof (ICalendarFrame))[] = ["saveRecords", "cancelChanges"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}
